import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { mktFetch, MARKETING_API } from './MarketingHubModal';
import {
  previewModeForDraft,
  assetUrl,
  TikTokPreview,
  LinkedInPostPreview,
  LinkedInArticlePreview,
  OpoNewsPreview,
} from './MarketingPlatformPreview.js';

const API = MARKETING_API;

// Marketing goals — each maps to a measurable metric we later pull back into the
// DB (marketing_asset_stats) to score whether the post achieved its objective.
// This is the labelled signal the self-learning loop trains on.
const GOALS = [
  { value: 'awareness', label: 'Awareness / Reach', metric: 'impressions', unit: 'impressions' },
  { value: 'engagement', label: 'Engagement', metric: 'engagements', unit: 'reactions + comments' },
  { value: 'traffic', label: 'Website traffic', metric: 'clicks', unit: 'link clicks' },
  { value: 'leads', label: 'Leads / Signups', metric: 'leads', unit: 'conversions' },
  { value: 'followers', label: 'Follower growth', metric: 'new_followers', unit: 'new followers' },
  { value: 'thought_leadership', label: 'Thought leadership', metric: 'comments', unit: 'meaningful comments' },
];

// Platform-native post styles selectable here and passed to Postiz.
const POST_STYLES = {
  linkedin: [
    { value: 'post', label: 'Standard post', format: 'post' },
    { value: 'article', label: 'Article', format: 'article' },
    { value: 'document', label: 'Document / carousel', format: 'document' },
    { value: 'poll', label: 'Poll', format: 'poll' },
  ],
  tiktok: [
    { value: 'video', label: 'Video', format: 'video' },
    { value: 'photo', label: 'Photo carousel', format: 'photo' },
  ],
  instagram: [
    { value: 'post', label: 'Feed post', format: 'post' },
    { value: 'reel', label: 'Reel', format: 'reel' },
    { value: 'story', label: 'Story', format: 'story' },
  ],
  opo_news: [
    { value: 'brief', label: 'News brief', format: 'post' },
    { value: 'update', label: 'Company update', format: 'post' },
    { value: 'announcement', label: 'Announcement', format: 'post' },
  ],
  default: [{ value: 'post', label: 'Standard post', format: 'post' }],
};

function platformLabel(p) {
  if (p === 'opo_news') return 'OPO NEWS';
  return p;
}

const LINKEDIN_VISIBILITY = [
  { value: 'public', label: 'Anyone (public)' },
  { value: 'connections', label: 'Connections only' },
];

function parseMeta(draft) {
  if (!draft) return {};
  if (typeof draft.metadata === 'string') {
    try { return JSON.parse(draft.metadata); } catch { return {}; }
  }
  return draft.metadata || {};
}

function parseJsonish(v, fallback) {
  if (v == null) return fallback;
  if (typeof v === 'object') return v;
  try { return JSON.parse(v); } catch { return fallback; }
}

function hashtagsToString(tags) {
  if (!tags) return '';
  const arr = Array.isArray(tags) ? tags : String(tags).split(/[\s,]+/);
  return arr.map((t) => `#${String(t).replace(/^#+/, '')}`).filter((t) => t !== '#').join(' ');
}

function stringToHashtags(str) {
  return String(str || '')
    .split(/[\s,]+/)
    .map((t) => t.trim().replace(/^#+/, ''))
    .filter(Boolean);
}

// datetime-local <-> ISO helpers (kept in the browser's local tz)
function isoToLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function localInputToIso(val) {
  if (!val) return null;
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export default function MarketingPublishModal({ draft, channels = [], onClose, onChanged }) {
  const meta = useMemo(() => parseMeta(draft), [draft]);

  const [title, setTitle] = useState(draft.title || '');
  const [body, setBody] = useState(draft.body || '');
  const [hashtags, setHashtags] = useState(hashtagsToString(draft.hashtags));
  const [hook, setHook] = useState(draft.hook || '');
  const [channelId, setChannelId] = useState(draft.channel_id || '');
  const [platform, setPlatform] = useState((draft.platform || draft.ch_platform || 'linkedin').toLowerCase());
  const [scheduledAt, setScheduledAt] = useState(isoToLocalInput(draft.scheduled_at));

  const initSettings = parseJsonish(draft.publish_settings, {});
  const [postStyle, setPostStyle] = useState(initSettings.post_style || (draft.format === 'article' ? 'article' : 'post'));
  const [visibility, setVisibility] = useState(initSettings.visibility || 'public');
  const [postAs, setPostAs] = useState(initSettings.post_as || 'personal');
  const [firstComment, setFirstComment] = useState(initSettings.first_comment || '');
  const [includeHashtags, setIncludeHashtags] = useState(initSettings.include_hashtags !== false);

  const initGoal = parseJsonish(draft.goal_target, {});
  const [goal, setGoal] = useState(draft.goal || '');
  const [goalValue, setGoalValue] = useState(initGoal.value != null ? String(initGoal.value) : '');

  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [publishState, setPublishState] = useState('');
  const [publishMsg, setPublishMsg] = useState('');
  const [err, setErr] = useState('');

  const saveTimer = useRef(null);
  const firstRender = useRef(true);
  const [channelList, setChannelList] = useState(channels);

  const styleOptions = POST_STYLES[platform] || POST_STYLES.default;
  const selectedStyle = styleOptions.find((s) => s.value === postStyle) || styleOptions[0];
  const derivedFormat = selectedStyle?.format || 'post';
  const goalDef = GOALS.find((g) => g.value === goal);

  useEffect(() => { setChannelList(channels); }, [channels]);

  // Refresh channels when the composer opens — backend auto-provisions OPO NEWS per identity.
  useEffect(() => {
    let cancelled = false;
    const idQ = draft.brand_id ? `&identity_id=${draft.brand_id}` : '';
    mktFetch(`${API}/platform/channels?x=1${idQ}`)
      .then((r) => r.json())
      .then((d) => { if (!cancelled && d.channels) setChannelList(d.channels); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [draft.id, draft.brand_id]);

  const channelOptions = useMemo(() => {
    const sameBrand = channelList.filter((c) => !draft.brand_id || c.brand_id === draft.brand_id);
    const list = sameBrand.length ? sameBrand : channelList;
    return [...list].sort((a, b) => {
      if (a.platform === 'opo_news' && b.platform !== 'opo_news') return -1;
      if (b.platform === 'opo_news' && a.platform !== 'opo_news') return 1;
      return String(a.display_name || a.platform).localeCompare(String(b.display_name || b.platform));
    });
  }, [channelList, draft.brand_id]);

  const selectedChannel = channelOptions.find((c) => String(c.id) === String(channelId));
  const isOpoNews = platform === 'opo_news';
  const connected = isOpoNews || selectedChannel?.connection_status === 'connected' || !!selectedChannel?.postiz_integration_id;

  // Preview should follow the user-selected post style, so drop any cached
  // graphic preview_mode and let platform + style decide.
  const previewFormat = derivedFormat === 'article' ? 'article' : 'post';
  const metaForPreview = useMemo(() => {
    const { graphic, ...rest } = meta || {};
    const cleanGraphic = graphic ? { ...graphic, preview_mode: undefined } : undefined;
    return cleanGraphic ? { ...rest, graphic: cleanGraphic } : rest;
  }, [meta]);

  const previewDraft = useMemo(() => ({
    ...draft,
    title,
    body,
    hashtags: stringToHashtags(hashtags),
    platform,
    format: previewFormat,
    metadata: metaForPreview,
  }), [draft, title, body, hashtags, platform, previewFormat, metaForPreview]);

  const previewMode = previewModeForDraft(previewDraft);
  const imageSrc = draft.asset_id ? assetUrl(draft.asset_id) : null;

  const buildPayload = useCallback(() => ({
    title,
    body,
    hook,
    hashtags: stringToHashtags(hashtags),
    platform,
    channel_id: channelId ? Number(channelId) : null,
    format: derivedFormat,
    scheduled_at: localInputToIso(scheduledAt),
    goal: goal || null,
    goal_target: goal && goalValue !== '' ? { metric: goalDef?.metric || goal, value: Number(goalValue) || 0, unit: goalDef?.unit } : null,
    publish_settings: {
      post_style: postStyle,
      visibility,
      post_as: postAs,
      first_comment: firstComment || undefined,
      include_hashtags: includeHashtags,
    },
  }), [title, body, hook, hashtags, platform, channelId, derivedFormat, scheduledAt, goal, goalValue, goalDef, postStyle, visibility, postAs, firstComment, includeHashtags]);

  const persist = useCallback(async () => {
    setSaveState('saving');
    setErr('');
    try {
      const res = await mktFetch(`${API}/platform/drafts/${draft.id}`, {
        method: 'PATCH',
        body: JSON.stringify(buildPayload()),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      setSaveState('saved');
    } catch (e) {
      setSaveState('error');
      setErr(e.message);
    }
  }, [draft.id, buildPayload]);

  // Debounced auto-save whenever any editable field changes.
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return undefined; }
    setSaveState('saving');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(persist, 800);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [persist]);

  // When the channel changes, follow its platform and reset the post style.
  const onChannelChange = (id) => {
    setChannelId(id);
    const ch = channelOptions.find((c) => String(c.id) === String(id));
    if (ch?.platform) {
      const p = ch.platform.toLowerCase();
      setPlatform(p);
      const opts = POST_STYLES[p] || POST_STYLES.default;
      if (!opts.find((s) => s.value === postStyle)) setPostStyle(opts[0].value);
    }
  };

  const publish = async (mode) => {
    if (publishState) return;
    setPublishState(mode);
    setPublishMsg('');
    setErr('');
    try {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      const payload = buildPayload();
      // For "Publish now", clear the schedule so the backend posts immediately.
      if (mode === 'now') payload.scheduled_at = null;
      const res = await mktFetch(`${API}/platform/drafts/${draft.id}/publish`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Publish failed');
      setSaveState('saved');
      setPublishMsg(data.message || (data.scheduled ? 'Scheduled.' : 'Sent to Postiz.'));
      onChanged?.();
    } catch (e) {
      setErr(e.message);
    } finally {
      setPublishState('');
    }
  };

  // Close on Escape; lock page scroll while the portal overlay is open.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const saveLabel = saveState === 'saving' ? 'Saving…'
    : saveState === 'saved' ? 'All changes saved'
    : saveState === 'error' ? 'Save failed' : '';

  const bodyCount = body.length;

  const modal = (
    <div className="mkt-publish-backdrop" role="presentation" onClick={onClose}>
      <div className="mkt-publish-modal" role="dialog" aria-modal="true" aria-label="Publish composer" onClick={(e) => e.stopPropagation()}>
        <div className="mkt-publish-head">
          <div className="mkt-publish-head-titles">
            <h3>PUBLISH COMPOSER</h3>
            <span className="mkt-publish-sub">{draft.brand_name} · {platformLabel(platform)}{selectedStyle ? ` · ${selectedStyle.label}` : ''}</span>
          </div>
          <div className="mkt-publish-head-right">
            <span className={`mkt-save-pill mkt-save-${saveState}`}>{saveLabel || 'Auto-save on'}</span>
            <button type="button" className="blog-tactical-action-btn blog-tactical-action-delete" onClick={onClose}>CLOSE</button>
          </div>
        </div>

        <div className="mkt-publish-body">
          {/* ── Left: live preview ── */}
          <div className="mkt-publish-preview-col">
            <span className="mkt-publish-col-label">LIVE PREVIEW</span>
            <div className="mkt-publish-preview-scroll">
              {previewMode === 'opo_news' && <OpoNewsPreview draft={previewDraft} imageSrc={imageSrc} full />}
              {previewMode === 'tiktok_feed' && <TikTokPreview draft={previewDraft} imageSrc={imageSrc} full />}
              {previewMode === 'linkedin_article' && <LinkedInArticlePreview draft={previewDraft} imageSrc={imageSrc} full />}
              {previewMode === 'linkedin_post' && <LinkedInPostPreview draft={previewDraft} imageSrc={imageSrc} full />}
            </div>
            {!imageSrc && (
              <p className="mkt-publish-hint">No graphic attached. Close and use GRAPHIC on the draft card to generate one.</p>
            )}
          </div>

          {/* ── Right: editor ── */}
          <div className="mkt-publish-edit-col">
            <span className="mkt-publish-col-label">POST DETAILS</span>

            <label className="marketing-integration-label">Channel
              <select value={channelId} onChange={(e) => onChannelChange(e.target.value)}>
                <option value="">— Select channel —</option>
                {channelOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.display_name || `${c.brand_name} · ${c.platform === 'opo_news' ? 'OPO NEWS' : c.platform}`}{(c.platform === 'opo_news' || c.connection_status === 'connected' || c.postiz_integration_id) ? ' ✓' : ''}
                  </option>
                ))}
              </select>
            </label>
            <p className={`mkt-publish-conn ${connected ? 'mkt-conn-ok' : 'mkt-conn-warn'}`}>
              {isOpoNews
                ? 'OPO NEWS — publishes to optiprime.ai and AI Navigation (no Postiz required).'
                : (connected ? 'Channel connected to Postiz — will auto-post.' : 'Channel not connected to Postiz — post will be queued for manual publishing.')}
            </p>

            <div className="mkt-publish-row">
              <label className="marketing-integration-label">Post style
                <select value={postStyle} onChange={(e) => setPostStyle(e.target.value)}>
                  {styleOptions.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </label>
              {platform === 'linkedin' && (
                <label className="marketing-integration-label">Visibility
                  <select value={visibility} onChange={(e) => setVisibility(e.target.value)}>
                    {LINKEDIN_VISIBILITY.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                  </select>
                </label>
              )}
            </div>

            {platform === 'linkedin' && (
              <label className="marketing-integration-label">Post as
                <select value={postAs} onChange={(e) => setPostAs(e.target.value)}>
                  <option value="personal">Personal profile</option>
                  <option value="company">Company page</option>
                </select>
              </label>
            )}

            {(platform === 'linkedin' || derivedFormat !== 'article') && (
              <label className="marketing-integration-label">Title / headline
                <input value={title} onChange={(e) => setTitle(e.target.value)} />
              </label>
            )}

            <label className="marketing-integration-label">Body
              <span className="marketing-prompt-char-count">{bodyCount} chars</span>
              <textarea rows={8} value={body} onChange={(e) => setBody(e.target.value)} />
            </label>

            <label className="marketing-integration-label">Hashtags
              <input value={hashtags} onChange={(e) => setHashtags(e.target.value)} placeholder="#realestate #bc" />
            </label>
            <label className="marketing-check-label">
              <input type="checkbox" checked={includeHashtags} onChange={(e) => setIncludeHashtags(e.target.checked)} />
              Append hashtags to the post body
            </label>

            <label className="marketing-integration-label">Hook (first line / opener)
              <input value={hook} onChange={(e) => setHook(e.target.value)} />
            </label>

            {!isOpoNews && (
            <label className="marketing-integration-label">First comment (optional)
              <textarea rows={2} value={firstComment} onChange={(e) => setFirstComment(e.target.value)} placeholder="Posted as the first comment — great for links." />
            </label>
            )}

            <div className="mkt-publish-divider"><span>GOAL &amp; MEASUREMENT</span></div>
            <p className="mkt-publish-hint">Set the objective and target. After posting we pull the real numbers back in to score performance — this is the signal the engine learns from.</p>
            <div className="mkt-publish-row">
              <label className="marketing-integration-label">Primary goal
                <select value={goal} onChange={(e) => setGoal(e.target.value)}>
                  <option value="">— No goal —</option>
                  {GOALS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                </select>
              </label>
              {goalDef && (
                <label className="marketing-integration-label">Target ({goalDef.unit})
                  <input type="number" min="0" value={goalValue} onChange={(e) => setGoalValue(e.target.value)} placeholder="e.g. 1000" />
                </label>
              )}
            </div>

            <div className="mkt-publish-divider"><span>SCHEDULE</span></div>
            <label className="marketing-integration-label">Schedule for (leave empty to post now)
              <input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
            </label>

            {err && <p className="marketing-msg-err">{err}</p>}
            {publishMsg && <p className="marketing-msg">{publishMsg}</p>}
          </div>
        </div>

        <div className="mkt-publish-footer">
          <span className="mkt-publish-footer-state">{saveLabel}</span>
          <div className="mkt-publish-footer-actions">
            <button type="button" className="blog-tactical-action-btn blog-tactical-action-view" disabled={!!publishState} onClick={persist}>SAVE</button>
            {scheduledAt ? (
              <button type="button" className="blog-tactical-action-btn blog-tactical-action-edit" disabled={!!publishState} onClick={() => publish('schedule')}>
                {publishState === 'schedule' ? 'SCHEDULING…' : 'SCHEDULE'}
              </button>
            ) : (
              <button type="button" className="blog-tactical-action-btn blog-tactical-action-edit" disabled={!!publishState} onClick={() => publish('now')}>
                {publishState === 'now' ? 'PUBLISHING…' : 'PUBLISH NOW'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
